import { markRaw, reactive } from 'vue';
import { Room, RoomEvent, Track, TrackEvent } from 'livekit-client';
import { callRequest, callStatus, callUrl } from '../api/internalCalls';

import {
  captureAudio,
  captureVideo,
  captureScreen,
  encodingForSource,
  screenConstraints,
} from './internalCallMedia';

// This state belongs to the application, not the current chat route.
// No token is persisted. The server, not this tab ID, authorizes every admission.
export function createInternalCallSession({
  makeRoom = () => new Room({ adaptiveStream: true, dynacast: true }),
  request = callRequest,
  status = callStatus,
  audio = captureAudio,
  video = captureVideo,
  screen = captureScreen,
  tabId = crypto.randomUUID(),
} = {}) {
  let noiseSuppression = true;
  try {
    noiseSuppression =
      localStorage.getItem('internal-call-noise-suppression') !== 'false';
  } catch {
    /* Storage may be unavailable in a restricted browser. */
  }
  const state = reactive({
    phase: 'idle',
    roomName: '',
    accountId: null,
    roomId: null,
    muted: true,
    paused: false,
    camera: false,
    screen: false,
    returnOffer: false,
    error: '',
    transferRequired: false,
    transferring: false,
    participants: [],
    playbackBlocked: false,
    canRemove: false,
    tracks: [],
    settings: {
      noiseSuppression,
      microphoneId: '',
      cameraId: '',
      outputId: 'default',
      deafened: false,
      screenAudio: false,
      quality: 'auto',
    },
  });
  let room;
  let generation;
  let url;
  let revision = 0;
  let queue = Promise.resolve();
  let timer;
  let container;
  let customerActive = false;
  const elements = new Map();
  const registered = new Map();
  const views = new Map();
  let nextTrackId = 0;
  const pendingTracks = new Set();
  const pendingMicrophones = new Set();
  const credentials = () => ({ tab_id: tabId, generation });
  const serialize = fn => {
    const result = queue.then(fn);
    queue = result.catch(() => {});
    return result;
  };
  const silence = () => {
    pendingTracks.forEach(track => track.stop());
    elements.forEach(el => {
      el.muted = true;
    });
    room?.localParticipant.trackPublications.forEach(pub => pub.track?.stop());
  };
  const detach = track => {
    const entry = registered.get(track);
    entry?.cleanup();
    registered.delete(track);
    state.tracks = state.tracks.filter(item => item.track !== track);
    elements.get(track)?.remove();
    elements.delete(track);
    views.get(track)?.forEach(el => el.remove());
    views.delete(track);
    track.detach?.()?.forEach(el => el.remove());
  };
  const clearElements = () => {
    [...registered.keys()].forEach(detach);
  };
  const syncPlayback = () => {
    elements.forEach(el => {
      el.muted =
        state.settings.deafened || state.paused || state.phase !== 'connected';
    });
  };
  const attachAudio = track => {
    if (!container || elements.has(track) || registered.get(track)?.local)
      return;
    const el = track.attach();
    el.autoplay = true;
    el.className = 'hidden';
    container.appendChild(el);
    elements.set(track, el);
    syncPlayback();
  };
  const attach = (
    track,
    local = false,
    name = '',
    source = track.source,
    identity = '',
    publicationId = track.sid
  ) => {
    if (
      registered.has(track) ||
      track.isMuted ||
      track.mediaStreamTrack?.readyState === 'ended'
    )
      return;
    // Subscription replacement can precede the old unsubscribe event.
    [...registered].forEach(([previous, entry]) => {
      if (
        source &&
        entry.source === source &&
        entry.local === local &&
        entry.identity === identity
      )
        detach(previous);
    });
    const owner = room;
    const ended = () => {
      if (room !== owner) return;
      detach(track);
      if (!local) return;
      if (source === Track.Source.Camera) state.camera = false;
      if (source === Track.Source.ScreenShare) state.screen = false;
      if (source === Track.Source.Microphone) state.muted = true;
      const companion =
        source === Track.Source.ScreenShare
          ? owner.localParticipant.getTrackPublication(
              Track.Source.ScreenShareAudio
            )?.track
          : undefined;
      serialize(async () => {
        if (room !== owner) return;
        await owner.localParticipant.unpublishTrack(track, true);
        if (companion) {
          detach(companion);
          await owner.localParticipant.unpublishTrack(companion, true);
        }
        if (source === Track.Source.Microphone && room === owner)
          await request(url, 'state', {
            ...credentials(),
            muted: true,
            paused: state.paused,
          });
      }).catch(() => {});
    };
    track.on?.(TrackEvent.Ended, ended);
    track.mediaStreamTrack?.addEventListener?.('ended', ended);
    registered.set(track, {
      local,
      identity,
      source,
      publicationId,
      cleanup: () => {
        track.off?.(TrackEvent.Ended, ended);
        track.mediaStreamTrack?.removeEventListener?.('ended', ended);
      },
    });
    if (track.kind === Track.Kind.Video) {
      nextTrackId += 1;
      state.tracks.push({
        id: `${nextTrackId}`,
        source,
        kind: track.kind,
        name: name || state.roomName,
        identity,
        local,
        track: markRaw(track),
      });
    } else attachAudio(track);
  };
  const attachTrack = (id, target) => {
    const descriptor = state.tracks.find(item => item.id === id);
    if (!descriptor || !target) return undefined;
    const { track } = descriptor;
    const el = track.attach();
    el.muted = true;
    el.autoplay = true;
    el.playsInline = true;
    el.className = 'h-full w-full object-contain';
    target.appendChild(el);
    if (!views.has(track)) views.set(track, new Set());
    views.get(track).add(el);
    return el;
  };
  const detachPublication = (pub, participant) => {
    if (pub.track) {
      detach(pub.track);
      return;
    }
    [...registered].forEach(([track, entry]) => {
      if (
        pub.trackSid
          ? entry.publicationId === pub.trackSid
          : pub.source &&
            entry.identity === participant?.identity &&
            entry.source === pub.source
      )
        detach(track);
    });
  };
  const detachView = (id, el) => {
    const track = state.tracks.find(item => item.id === id)?.track;
    track?.detach(el);
    views.get(track)?.delete(el);
    el?.remove();
  };
  const refreshParticipants = () => {
    if (!room) return;
    state.participants = [
      room.localParticipant,
      ...room.remoteParticipants.values(),
    ].map(p => ({
      identity: p.identity,
      name: p.name || p.identity,
      local: p === room.localParticipant,
    }));
  };
  const stopMedia = async () => {
    if (!room) return;
    const publications = [...room.localParticipant.trackPublications.values()];
    publications.forEach(pub => pub.track && detach(pub.track));
    await Promise.all(
      publications.map(pub =>
        pub.track
          ? room.localParticipant.unpublishTrack(pub.track, true)
          : undefined
      )
    );
    state.camera = false;
    state.screen = false;
  };
  const close = async (notify = true) => {
    revision += 1;
    state.error = '';
    state.transferRequired = false;
    state.transferring = false;
    state.playbackBlocked = false;
    state.paused = false;
    silence();
    clearInterval(timer);
    state.returnOffer = false;
    state.phase = 'closing';
    const oldUrl = url;
    const oldCredentials = credentials();
    generation = undefined;
    const oldRoom = room;
    room = undefined;
    clearElements();
    state.participants = [];
    state.camera = false;
    state.screen = false;
    try {
      await oldRoom?.disconnect(true);
      if (notify && oldUrl && oldCredentials.generation)
        await request(oldUrl, 'leave', oldCredentials);
    } finally {
      state.phase = 'idle';
    }
  };
  const revalidate = async () => {
    const checkedRoom = room;
    const checkedGeneration = generation;
    try {
      const result = await request(url, 'revalidate', credentials());
      if (room !== checkedRoom || generation !== checkedGeneration)
        return false;
      if (result.authorized !== true) throw new Error('access_revoked');
      return true;
    } catch {
      if (room !== checkedRoom || generation !== checkedGeneration)
        return false;
      await close(false);
      state.error = 'access_revoked';
      return false;
    }
  };
  const publish = async (factory, source) => {
    const expected = revision;
    const owner = room;
    const tracks = await factory({ ...state.settings });
    const list = Array.isArray(tracks) ? tracks : [tracks];
    if (expected !== revision || state.paused || state.phase !== 'connected') {
      list.forEach(track => track.stop());
      return;
    }
    const results = await Promise.allSettled(
      list.map(async track => {
        pendingTracks.add(track);
        if (source === Track.Source.Microphone) pendingMicrophones.add(track);
        try {
          if (expected !== revision || state.paused || !room) track.stop();
          else {
            const trackSource =
              source === Track.Source.ScreenShare &&
              track.kind === Track.Kind.Audio
                ? Track.Source.ScreenShareAudio
                : source;
            await owner.localParticipant.publishTrack(track, {
              source: trackSource,
              ...encodingForSource(trackSource, state.settings.quality),
            });
            if (
              expected !== revision ||
              state.paused ||
              track.mediaStreamTrack?.readyState === 'ended'
            ) {
              detach(track);
              track.stop();
              await owner.localParticipant.unpublishTrack(track, true);
            } else
              attach(
                track,
                true,
                room.localParticipant.name,
                trackSource,
                room.localParticipant.identity
              );
          }
        } catch (error) {
          track.stop();
          throw error;
        } finally {
          pendingTracks.delete(track);
          pendingMicrophones.delete(track);
        }
      })
    );
    const failure = results.find(result => result.status === 'rejected');
    if (failure) {
      await Promise.allSettled(
        list.map(track => {
          detach(track);
          track.stop();
          return owner.localParticipant.unpublishTrack(track, true);
        })
      );
      throw failure.reason;
    }
  };
  const publishMicrophone = async () => {
    const expected = revision;
    try {
      await publish(audio, Track.Source.Microphone);
    } catch (error) {
      if (expected === revision && room) {
        state.muted = true;
        await request(url, 'state', {
          ...credentials(),
          muted: true,
          paused: state.paused,
        }).catch(() => {});
      }
      throw error;
    }
  };
  const start = async (accountId, selectedRoom, transfer = false) => {
    if (state.phase !== 'idle') return;
    if (customerActive) return;
    url = callUrl(accountId, selectedRoom.id);
    state.accountId = accountId;
    state.roomId = selectedRoom.id;
    state.roomName = selectedRoom.name;
    state.error = '';
    state.transferring = transfer;
    state.phase = 'connecting';
    revision += 1;
    const attempt = revision;
    const attemptUrl = url;
    try {
      const ticket = await request(url, 'join', { tab_id: tabId, transfer });
      if (attempt !== revision || customerActive) {
        await request(attemptUrl, 'leave', {
          tab_id: tabId,
          generation: ticket.generation,
        });
        if (state.phase === 'connecting' && !room) state.phase = 'idle';
        return;
      }
      generation = ticket.generation;
      state.muted = !transfer || ticket.muted !== false;
      state.paused = Boolean(ticket.paused);
      state.canRemove = Boolean(ticket.permissions?.remove_participant);
      room = makeRoom();
      const joiningRoom = room;
      const on = (event, callback) =>
        joiningRoom.on(event, (...args) => {
          if (room === joiningRoom) return callback(...args);
          return undefined;
        });
      on(RoomEvent.TrackSubscribed, (track, publication, participant) =>
        attach(
          track,
          false,
          participant?.name || participant?.identity,
          publication?.source,
          participant?.identity,
          publication?.trackSid
        )
      );
      on(
        RoomEvent.LocalTrackPublished,
        pub =>
          pub.track &&
          attach(
            pub.track,
            true,
            joiningRoom.localParticipant.name,
            pub.source,
            joiningRoom.localParticipant.identity,
            pub.trackSid
          )
      );
      on(RoomEvent.LocalTrackUnpublished, pub => {
        detachPublication(pub, joiningRoom.localParticipant);
        const active = state.phase === 'connected' && !state.paused;
        state.camera =
          active && Boolean(room?.localParticipant.isCameraEnabled);
        state.screen =
          active && Boolean(room?.localParticipant.isScreenShareEnabled);
      });
      on(RoomEvent.TrackUnsubscribed, detach);
      on(RoomEvent.TrackUnpublished, detachPublication);
      on(RoomEvent.TrackMuted, pub => {
        if (pub.track) detach(pub.track);
      });
      on(RoomEvent.TrackUnmuted, (pub, participant) => {
        if (pub.track)
          attach(
            pub.track,
            participant === joiningRoom.localParticipant,
            participant?.name || participant?.identity,
            pub.source,
            participant?.identity,
            pub.trackSid
          );
      });
      on(RoomEvent.ParticipantConnected, refreshParticipants);
      on(RoomEvent.ParticipantDisconnected, participant => {
        [...registered].forEach(([track, entry]) => {
          if (entry.identity === participant.identity) detach(track);
        });
        refreshParticipants();
      });
      on(RoomEvent.AudioPlaybackStatusChanged, () => {
        state.playbackBlocked = !room?.canPlaybackAudio;
      });
      const suspendForReconnect = () => {
        revision += 1;
        silence();
        state.phase = 'reconnecting';
        state.camera = false;
        state.screen = false;
      };
      on(RoomEvent.Reconnecting, suspendForReconnect);
      on(RoomEvent.SignalReconnecting, suspendForReconnect);
      on(RoomEvent.Reconnected, async () => {
        // Signal-only recovery also emits Reconnected. Stop and unpublish even
        // when no media Reconnecting event was delivered before this callback.
        suspendForReconnect();
        const expected = revision;
        await serialize(stopMedia);
        if (!room || expected !== revision) return;
        if (await revalidate()) {
          if (expected !== revision) return;
          // Never reacquire a device automatically after a network interruption.
          state.phase = 'connected';
          state.paused = true;
          state.returnOffer = !customerActive;
        }
      });
      on(RoomEvent.Disconnected, () => {
        if (room === joiningRoom) close(false);
      });
      await joiningRoom.connect(ticket.url, ticket.token, {
        autoSubscribe: true,
      });
      if (room !== joiningRoom || attempt !== revision) {
        if (room === joiningRoom) await close();
        else await joiningRoom.disconnect(true);
        return;
      }
      if (customerActive) state.paused = true;
      state.phase = 'connected';
      state.transferRequired = false;
      state.transferring = false;
      if (state.settings.outputId !== 'default') {
        try {
          const changed = await joiningRoom.switchActiveDevice(
            'audiooutput',
            state.settings.outputId
          );
          if (changed === false) throw new Error('output_device_failed');
        } catch {
          if (room === joiningRoom) {
            state.settings.outputId = 'default';
            state.error = 'media_failed';
          }
        }
        if (room !== joiningRoom || attempt !== revision) return;
      }
      syncPlayback();
      refreshParticipants();
      timer = setInterval(revalidate, 5000);
      // Initial admission is muted; a transfer only restores audio after explicit entry.
      if (!state.paused && !state.muted) await serialize(publishMicrophone);
    } catch (error) {
      if (attempt !== revision) return;
      const code = error.response?.data?.error;
      const forbidden = [401, 403].includes(error.response?.status);
      await close(false);
      state.transferRequired =
        code === 'transfer_required' || (transfer && !forbidden);
      state.error = code === 'transfer_required' ? code : 'connection_failed';
      if (forbidden) state.error = 'access_revoked';
    }
  };
  const setMuted = muted => {
    if (!room || state.paused || state.phase !== 'connected')
      return Promise.resolve();
    // Stopping local input must not wait for the API or a pending capture dialog.
    if (muted) {
      revision += 1;
      state.muted = true;
      pendingMicrophones.forEach(track => track.stop());
      room.localParticipant
        .getTrackPublication(Track.Source.Microphone)
        ?.track?.stop();
    }
    const expected = revision;
    return serialize(async () => {
      if (state.paused || expected !== revision || !room) return;
      if (
        !muted &&
        !state.muted &&
        room.localParticipant.getTrackPublication(Track.Source.Microphone)
          ?.track
      )
        return;
      if (muted) {
        const pub = room.localParticipant.getTrackPublication(
          Track.Source.Microphone
        );
        if (pub?.track) {
          detach(pub.track);
          await room.localParticipant.unpublishTrack(pub.track, true);
        }
      }
      await request(url, 'state', { ...credentials(), muted, paused: false });
      if (state.paused || expected !== revision || !room) return;
      if (!muted) await publishMicrophone();
      if (expected === revision && !state.paused && room) {
        state.muted =
          muted ||
          !room.localParticipant.getTrackPublication(Track.Source.Microphone)
            ?.track;
      }
    });
  };
  const toggleVisual = kind => {
    const expected = revision;
    return serialize(async () => {
      if (
        expected !== revision ||
        !room ||
        state.paused ||
        state.phase !== 'connected'
      )
        return;
      const source =
        kind === 'camera' ? Track.Source.Camera : Track.Source.ScreenShare;
      const pub = room.localParticipant.getTrackPublication(source);
      if (pub?.track) {
        detach(pub.track);
        await room.localParticipant.unpublishTrack(pub.track, true);
      } else await publish(kind === 'camera' ? video : screen, source);
      if (kind === 'screen' && pub?.track) {
        const companion = room?.localParticipant.getTrackPublication(
          Track.Source.ScreenShareAudio
        )?.track;
        if (companion) {
          detach(companion);
          await room.localParticipant.unpublishTrack(companion, true);
        }
      }
      state[kind] = Boolean(
        room?.localParticipant.getTrackPublication(source)?.track
      );
    });
  };
  const replaceScreen = () => {
    const expected = revision;
    return serialize(async () => {
      if (
        !room ||
        state.paused ||
        state.phase !== 'connected' ||
        expected !== revision
      )
        return;
      // A cancelled chooser keeps the current screen running.
      const captured = await screen({ ...state.settings });
      const list = Array.isArray(captured) ? captured : [captured];
      if (!room || state.paused || expected !== revision) {
        list.forEach(track => track.stop());
        return;
      }
      list.forEach(track => pendingTracks.add(track));
      try {
        await Promise.all(
          [Track.Source.ScreenShare, Track.Source.ScreenShareAudio].map(
            async source => {
              const track =
                room.localParticipant.getTrackPublication(source)?.track;
              if (track) {
                detach(track);
                await room.localParticipant.unpublishTrack(track, true);
              }
            }
          )
        );
        if (!room || state.paused || expected !== revision) {
          list.forEach(track => track.stop());
          return;
        }
        await publish(() => list, Track.Source.ScreenShare);
        state.screen = Boolean(
          room?.localParticipant.getTrackPublication(Track.Source.ScreenShare)
            ?.track
        );
      } catch (error) {
        list.forEach(track => track.stop());
        throw error;
      } finally {
        list.forEach(track => pendingTracks.delete(track));
      }
    });
  };
  const toggleDeafened = () => {
    state.settings.deafened = !state.settings.deafened;
    syncPlayback();
  };
  const updateSettings = patch => {
    const expected = revision;
    return serialize(async () => {
      if (expected !== revision) return;
      const next = { ...state.settings, ...patch };
      if (
        room &&
        patch.outputId !== undefined &&
        patch.outputId !== state.settings.outputId
      ) {
        const changed = await room.switchActiveDevice(
          'audiooutput',
          patch.outputId
        );
        if (changed === false) throw new Error('output_device_failed');
      }
      if (expected !== revision) return;
      const active = room && !state.paused && state.phase === 'connected';
      if (active) {
        const microphoneChanged =
          next.microphoneId !== state.settings.microphoneId ||
          next.noiseSuppression !== state.settings.noiseSuppression;
        const cameraChanged = next.cameraId !== state.settings.cameraId;
        const replacements = [
          [microphoneChanged && !state.muted, Track.Source.Microphone, audio],
          [cameraChanged && state.camera, Track.Source.Camera, video],
        ];
        await replacements.reduce(
          async (previousReplacement, [changed, source, factory]) => {
            await previousReplacement;
            if (changed) {
              const captured = await factory(next);
              if (expected !== revision || state.paused || !room) {
                captured.stop();
                return;
              }
              pendingTracks.add(captured);
              if (source === Track.Source.Microphone)
                pendingMicrophones.add(captured);
              try {
                const previous =
                  room.localParticipant.getTrackPublication(source)?.track;
                if (previous) {
                  detach(previous);
                  await room.localParticipant.unpublishTrack(previous, true);
                }
                if (expected !== revision || state.paused || !room) {
                  captured.stop();
                  return;
                }
                await publish(() => captured, source);
              } catch (error) {
                captured.stop();
                if (
                  source === Track.Source.Microphone &&
                  expected === revision &&
                  room
                ) {
                  state.muted = true;
                  await request(url, 'state', {
                    ...credentials(),
                    muted: true,
                    paused: state.paused,
                  }).catch(() => {});
                }
                throw error;
              } finally {
                pendingTracks.delete(captured);
                pendingMicrophones.delete(captured);
              }
            }
          },
          Promise.resolve()
        );
        if (expected !== revision || state.paused || !room) return;
        const shared = room?.localParticipant.getTrackPublication(
          Track.Source.ScreenShare
        )?.track;
        if (shared && next.quality !== state.settings.quality) {
          const applyQuality = async quality => {
            await shared.mediaStreamTrack.applyConstraints(
              screenConstraints(quality)
            );
            if (expected !== revision || state.paused || !room) return;
            shared.mediaStreamTrack.contentHint =
              quality === 'motion' ? 'motion' : 'detail';
            await shared.setDegradationPreference(
              encodingForSource(Track.Source.ScreenShare, quality)
                .degradationPreference
            );
          };
          try {
            await applyQuality(next.quality);
          } catch (error) {
            if (expected === revision && !state.paused && room)
              await applyQuality(state.settings.quality).catch(() => {});
            throw error;
          }
        }
      }
      if (expected !== revision) return;
      Object.assign(state.settings, next);
      syncPlayback();
      try {
        localStorage.setItem(
          'internal-call-noise-suppression',
          String(next.noiseSuppression)
        );
      } catch {
        /* Keep the setting for this session. */
      }
    });
  };
  const pauseForCustomer = async () => {
    customerActive = true;
    revision += 1;
    state.returnOffer = false;
    state.paused = true;
    silence();
    await serialize(stopMedia);
    if (room)
      await request(url, 'state', {
        ...credentials(),
        muted: state.muted,
        paused: true,
      });
  };
  const customerEnded = async () => {
    if (!customerActive) return;
    customerActive = false;
    const expected = revision;
    if (
      room &&
      (await revalidate()) &&
      !customerActive &&
      expected === revision
    )
      state.returnOffer = true;
  };
  const resume = async () => {
    state.returnOffer = false;
    const expected = revision;
    if (
      customerActive ||
      !room ||
      !(await revalidate()) ||
      expected !== revision
    )
      return;
    await serialize(async () => {
      if (customerActive || expected !== revision) return;
      await stopMedia();
      await request(url, 'state', {
        ...credentials(),
        muted: state.muted,
        paused: false,
      });
      if (customerActive || expected !== revision) return;
      state.paused = false;
      syncPlayback();
      if (!state.muted) await publishMicrophone();
    });
  };
  const recoverPlayback = async () => {
    const playbackRoom = room;
    const expected = revision;
    if (!playbackRoom || state.paused || state.phase !== 'connected') return;
    try {
      await playbackRoom.switchActiveDevice(
        'audiooutput',
        state.settings.outputId
      );
    } catch {
      /* Browser may not support output selection. */
    }
    if (expected !== revision || state.paused) return;
    await playbackRoom.startAudio();
    if (expected !== revision || state.paused) return;
    state.playbackBlocked = !playbackRoom.canPlaybackAudio;
    syncPlayback();
  };
  const dismissError = () => {
    state.error = '';
  };
  // Feedback belongs to the session in which the action was requested. A late
  // permission rejection must not reopen the bar after leaving or transferring.
  const run = async (action, errorCode = 'media_failed') => {
    let expected = revision;
    try {
      const result = action();
      expected = revision;
      await result;
      if (expected === revision && state.error === errorCode) dismissError();
    } catch {
      if (expected === revision && state.phase === 'connected')
        state.error = errorCode;
    }
  };
  return {
    state,
    run,
    dismissError,
    start,
    close,
    setMuted,
    toggleVisual,
    replaceScreen,
    toggleDeafened,
    updateSettings,
    pauseForCustomer,
    customerEnded,
    resume,
    recoverPlayback,
    status: () => status(url),
    getUrl: () => url,
    attachTrack,
    detachView,
    setContainer(el) {
      container = el;
      if (el)
        registered.forEach((entry, track) => {
          if (track.kind === Track.Kind.Audio) {
            if (elements.has(track)) el.appendChild(elements.get(track));
            else attachAudio(track);
          }
        });
    },
  };
}

export const internalCall = createInternalCallSession();

// Future incoming/outgoing/automatic customer-media paths MUST await this bridge
// before starting media. It does not enable or call a customer provider.
export const beforeCustomerMedia = () => internalCall.pauseForCustomer();
export const afterCustomerMedia = () => internalCall.customerEnded();
